/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Spaces", description = "Use Spaces to partition a single Hightouch workspace into isolated sub-workspaces for different regions, brands, teams, or business units — while sharing the same underlying data models and schema.", lastUpdatedDate = new Date(1790553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Spaces",
        description: "Use Spaces to partition a single Hightouch workspace into isolated sub-workspaces for different regions, brands, teams, or business units — while sharing the same underlying data models and schema.",
        lastUpdatedDate: "Mon Sep 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "How Spaces work",
          "depth": 0,
          "id": "how-spaces-work"
        }, {
          "value": "Before you begin",
          "depth": 0,
          "id": "before-you-begin"
        }, {
          "value": "Common Space strategies",
          "depth": 1,
          "id": "common-space-strategies"
        }, {
          "value": "Key design principles",
          "depth": 1,
          "id": "key-design-principles"
        }, {
          "value": "Request access",
          "depth": 0,
          "id": "request-access"
        }, {
          "value": "Create a Space",
          "depth": 0,
          "id": "create-a-space"
        }, {
          "value": "Associate resources with a Space",
          "depth": 0,
          "id": "associate-resources-with-a-space"
        }, {
          "value": "Models and schema",
          "depth": 1,
          "id": "models-and-schema"
        }, {
          "value": "Audiences",
          "depth": 1,
          "id": "audiences"
        }, {
          "value": "Syncs",
          "depth": 1,
          "id": "syncs"
        }, {
          "value": "Assign user groups to a Space",
          "depth": 0,
          "id": "assign-user-groups-to-a-space"
        }, {
          "value": "From the Spaces settings",
          "depth": 1,
          "id": "from-the-spaces-settings"
        }, {
          "value": "From the Groups settings",
          "depth": 1,
          "id": "from-the-groups-settings"
        }, {
          "value": "Working in Spaces",
          "depth": 0,
          "id": "working-in-spaces"
        }, {
          "value": "Switching between Spaces",
          "depth": 1,
          "id": "switching-between-spaces"
        }, {
          "value": "What local or Space-scoped teams can see and do",
          "depth": 1,
          "id": "what-local-or-space-scoped-teams-can-see-and-do"
        }, {
          "value": "What central or global teams can see and do",
          "depth": 1,
          "id": "what-central-or-global-teams-can-see-and-do"
        }, {
          "value": "Governance patterns",
          "depth": 0,
          "id": "governance-patterns"
        }, {
          "value": "Global brand with local operators",
          "depth": 1,
          "id": "global-brand-with-local-operators"
        }, {
          "value": "Multi-brand or multi-region organization",
          "depth": 1,
          "id": "multi-brand-or-multi-region-organization"
        }, {
          "value": "Multi-team organization (separate teams, shared customers)",
          "depth": 1,
          "id": "multi-team-organization-separate-teams-shared-customers"
        }, {
          "value": "Naming conventions",
          "depth": 0,
          "id": "naming-conventions"
        }, {
          "value": "Current limitations",
          "depth": 0,
          "id": "current-limitations"
        }, {
          "value": "Related docs",
          "depth": 0,
          "id": "related-docs"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      strong: "strong",
      ul: "ul",
      li: "li",
      a: "a",
      hr: "hr",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      img: "img",
      h3: "h3",
      code: "code",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsx(_components.p, {
          children: "Spaces is currently in early access. Contact your Hightouch team to request\nenablement for your workspace."
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Spaces"
        }), " let you logically partition a single Hightouch workspace into multiple isolated sub-workspaces for different regions, brands, teams, or business units — while sharing the same underlying data models, schema, sources, and destinations."]
      }), "\n", _jsx(_components.p, {
        children: "This is useful when you have multiple teams that need to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Build and run campaigns independently without seeing each other's work"
        }), "\n", _jsx(_components.li, {
          children: "Stay within a compliant, governed environment without accessing data outside their purview"
        }), "\n", _jsx(_components.li, {
          children: "Work from a single shared customer schema rather than maintaining separate workspaces"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Spaces solve a specific problem: you can already control ", _jsx(_components.strong, {
          children: "what data"
        }), " a team can query (via ", _jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: "subsets"
        }), ") and ", _jsx(_components.strong, {
          children: "where"
        }), " they can send it (via ", _jsx(_components.a, {
          href: "/customer-studio/destination-rules",
          children: "destination rules"
        }), "), but without Spaces, all teams could still see and potentially edit each other's audiences and syncs."]
      }), "\n", _jsx(_components.p, {
        children: "Spaces close that gap by making audiences and syncs visible only within the Space they belong to."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-spaces-work",
        children: "How Spaces work"
      }), "\n", _jsx(_components.p, {
        children: "Spaces introduce a lightweight partitioning layer on top of a shared workspace:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Layer"
            }), _jsx(_components.th, {
              children: "Shared or scoped?"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Sources"
            }), _jsx(_components.td, {
              children: "Shared across all Spaces"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Destinations"
            }), _jsx(_components.td, {
              children: "Shared across all Spaces"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Schema and models"
            }), _jsx(_components.td, {
              children: "Shared across all Spaces"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Audiences"
            }), _jsx(_components.td, {
              children: "Scoped to a specific Space"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Syncs"
            }), _jsx(_components.td, {
              children: "Scoped to a specific Space"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Users access a Space by selecting it from the workspace/Space dropdown in the Hightouch UI. The interface then filters to show only the audiences and syncs that belong to that Space."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/spaces/spaces-full-workspace-access.png",
          alt: "Dropdown to switch between Spaces"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Workspace admins"
        }), " retain cross-Space visibility and can review, troubleshoot, and audit resources across all Spaces."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Space-scoped users"
        }), " (those whose user groups are assigned only to a specific Space) can only see and edit resources within that Space. They cannot view or modify audiences or syncs owned by other Spaces — even if the underlying data models are shared."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "before-you-begin",
        children: "Before you begin"
      }), "\n", _jsx(_components.p, {
        children: "Spaces is in early access and requires enablement by your Hightouch team. Before requesting access, align internally on how you want to structure your organization within Spaces. Your Hightouch team can help you validate your design."
      }), "\n", _jsx(_components.h3, {
        id: "common-space-strategies",
        children: "Common Space strategies"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Strategy"
            }), _jsx(_components.th, {
              children: "Example"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "By region or country"
            }), _jsx(_components.td, {
              children: "US, UK, EU, APAC"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "By brand"
            }), _jsx(_components.td, {
              children: "Brand A, Brand B"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "By local operator or venue"
            }), _jsx(_components.td, {
              children: "Individual restaurants, hotels, stores"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "By team or function"
            }), _jsx(_components.td, {
              children: "Lifecycle, Paid Media, CRM"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "By environment"
            }), _jsx(_components.td, {
              children: "Beta (experimentation), Production (live campaigns)"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "key-design-principles",
        children: "Key design principles"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["All Spaces in a workspace ", _jsxs(_components.strong, {
            children: ["share the same ", _jsx(_components.a, {
              href: "/customer-studio/schema",
              children: "schema"
            }), " and models"]
          }), " — there is no per-Space schema isolation in the current release. See ", _jsx(_components.a, {
            href: "/customer-studio/data-model",
            children: "Plan your data model"
          }), " for guidance on designing a schema that serves multiple teams."]
        }), "\n", _jsxs(_components.li, {
          children: ["Spaces can function as pure ", _jsx(_components.strong, {
            children: "governance and UX partitions"
          }), ": even when all teams share the same data models and destinations, Spaces prevent teams from seeing or modifying each other's campaign assets"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Models should include a consistent filter field"
          }), " (for example, ", _jsx(_components.code, {
            children: "country_code"
          }), ", ", _jsx(_components.code, {
            children: "brand_code"
          }), ", ", _jsx(_components.code, {
            children: "business_unit_id"
          }), ", or ", _jsx(_components.code, {
            children: "operator_id"
          }), ") so you can apply subsets or audience filters within each Space"]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "request-access",
        children: "Request access"
      }), "\n", _jsx(_components.p, {
        children: "To enable Spaces for your workspace:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Contact your Hightouch team."
        }), "\n", _jsxs(_components.li, {
          children: ["Provide the following:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Which workspace should have Spaces enabled (workspace link or ID)"
            }), "\n", _jsxs(_components.li, {
              children: ["The field(s) you use to distinguish business units or teams (for example, ", _jsx(_components.code, {
                children: "business_unit_id"
              }), ", ", _jsx(_components.code, {
                children: "country_code"
              }), ", ", _jsx(_components.code, {
                children: "venue_id"
              }), ")"]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once enabled, Spaces will appear under ", _jsx(_components.strong, {
          children: "Settings → Workspace → Spaces"
        }), " in the Hightouch UI."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-a-space",
        children: "Create a Space"
      }), "\n", _jsx(_components.p, {
        children: "Once Spaces is enabled:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Settings → Workspace"
          }), " in Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Spaces"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Create Space"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give the Space a ", _jsx(_components.strong, {
            children: "name"
          }), " (for example, ", _jsx(_components.code, {
            children: "UK"
          }), ", ", _jsx(_components.code, {
            children: "Brand A"
          }), ", ", _jsx(_components.code, {
            children: "Lifecycle"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Optionally add a ", _jsx(_components.strong, {
            children: "description"
          }), " to clarify ownership and scope."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Repeat for each Space you need."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/spaces/spaces-overview-page.png",
          alt: "Create a Space"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "associate-resources-with-a-space",
        children: "Associate resources with a Space"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Audiences and syncs"
        }), " are ", _jsx(_components.strong, {
          children: "scoped to the Space they are created in"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "models-and-schema",
        children: "Models and schema"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Models, schema, sources, and destinations"
        }), " are ", _jsx(_components.strong, {
          children: "configured at the workspace level"
        }), " and are ", _jsx(_components.strong, {
          children: "shared across all Spaces"
        }), ". No additional setup is required at the Space level for these resources."]
      }), "\n", _jsxs(_components.p, {
        children: ["Confirm that your models include the filter field you plan to use to distinguish each Space's data (for example, ", _jsx(_components.code, {
          children: "country_code"
        }), " or ", _jsx(_components.code, {
          children: "brand_code"
        }), "). This field will be used to apply ", _jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: "subsets"
        }), " or audience-level filters within each Space."]
      }), "\n", _jsx(_components.h3, {
        id: "audiences",
        children: "Audiences"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Switch to the correct Space using the workspace/Space dropdown."
        }), "\n", _jsx(_components.li, {
          children: "Build your audience as usual in Customer Studio."
        }), "\n", _jsxs(_components.li, {
          children: ["If you want strict data-level separation, apply required ", _jsx(_components.a, {
            href: "/customer-studio/subsets",
            children: "subsets"
          }), " or audience filters (for example, ", _jsx(_components.code, {
            children: "country_code = 'UK'"
          }), ") so the audience only pulls records relevant to that Space."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The audience will be owned by — and visible only within — the Space it was created in."
      }), "\n", _jsx(_components.h3, {
        id: "syncs",
        children: "Syncs"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Create the sync from within the same Space as the audience."
        }), "\n", _jsx(_components.li, {
          children: "The sync will be scoped to that Space and visible only to user groups with access to it."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "assign-user-groups-to-a-space",
        children: "Assign user groups to a Space"
      }), "\n", _jsxs(_components.p, {
        children: ["Access to a Space is ", _jsx(_components.strong, {
          children: "controlled through user groups"
        }), ", not individual users. Only users who belong to a user group attached to a Space can see or edit that Space's audiences and syncs."]
      }), "\n", _jsx(_components.p, {
        children: "There are two ways to assign user groups to a Space."
      }), "\n", _jsx(_components.h3, {
        id: "from-the-spaces-settings",
        children: "From the Spaces settings"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Settings → Workspace → Spaces"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Create or select a Space."
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "User groups"
          }), " section in the Space configuration."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the user groups that should have access to this Space (for example, ", _jsx(_components.code, {
            children: "UK Marketing"
          }), ", ", _jsx(_components.code, {
            children: "Brand A CRM"
          }), ", ", _jsx(_components.code, {
            children: "Global Admins"
          }), ")."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "from-the-groups-settings",
        children: "From the Groups settings"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Settings → Workspaces → Groups"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Edit an existing group or create a new one."
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Spaces"
          }), " section of the group configuration, assign the Space(s) this group should have access to."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "User group membership is managed separately in your existing RBAC or user management settings, or via SSO/SCIM if configured."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/spaces/spaces-groups.png",
          alt: "User groups assigned to a Space"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "working-in-spaces",
        children: "Working in Spaces"
      }), "\n", _jsx(_components.p, {
        children: "Once configured, Spaces function as a filtered view of Customer Studio for each team."
      }), "\n", _jsx(_components.h3, {
        id: "switching-between-spaces",
        children: "Switching between Spaces"
      }), "\n", _jsx(_components.p, {
        children: "Use the workspace/Space dropdown to select the Space you want to work in. The UI scopes visible resources to that Space:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audiences"
          }), " — only those created in the selected Space"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " — only those owned by that Space"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This reduces the risk of accidentally touching another team's campaigns or violating compliance rules between legal entities or brands."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/spaces/spaces-full-workspace-access.png",
          alt: "Dropdown to switch between Spaces"
        })
      }), "\n", _jsx(_components.h3, {
        id: "what-local-or-space-scoped-teams-can-see-and-do",
        children: "What local or Space-scoped teams can see and do"
      }), "\n", _jsx(_components.p, {
        children: "Teams with access to a single Space:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "See only the audiences, syncs, and related resources within their Space"
        }), "\n", _jsx(_components.li, {
          children: "Cannot see or modify audiences or syncs that belong to other Spaces"
        }), "\n", _jsx(_components.li, {
          children: "Build and manage their own campaigns using either a Space-specific subset of data, or the full shared schema filtered by their Space's configuration"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "what-central-or-global-teams-can-see-and-do",
        children: "What central or global teams can see and do"
      }), "\n", _jsx(_components.p, {
        children: "Teams with access to multiple Spaces (or all Spaces):"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Can review and audit local audiences and syncs across Spaces"
        }), "\n", _jsx(_components.li, {
          children: "Can help troubleshoot issues for specific Spaces"
        }), "\n", _jsx(_components.li, {
          children: "Continue to own the shared schema, models, and global governance rules (naming conventions, consent, promotion workflows)"
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "governance-patterns",
        children: "Governance patterns"
      }), "\n", _jsx(_components.h3, {
        id: "global-brand-with-local-operators",
        children: "Global brand with local operators"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Scenario:"
        }), " A global brand operates many local entities (for example, restaurants, venues, or stores). Each operator needs a simple audience builder for local campaigns, while the central team maintains control over data and governance."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Pattern:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Create one Space per operator or logical cluster of operators"
        }), "\n", _jsx(_components.li, {
          children: "Keep a Global or Support Space for the central team with full visibility"
        }), "\n", _jsx(_components.li, {
          children: "Central team owns models, schema, and guardrails; local Spaces build and run their own audiences and syncs"
        }), "\n", _jsx(_components.li, {
          children: "Local teams have no visibility into other operators' campaign assets"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This reduces maintenance overhead — one workspace and schema — while giving each operator a focused, compliant experience."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "multi-brand-or-multi-region-organization",
        children: "Multi-brand or multi-region organization"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Scenario:"
        }), " A single data platform powers multiple brands or regions, each with its own marketing team and campaigns."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Pattern:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Create one Space per brand or region (for example, ", _jsx(_components.code, {
            children: "Brand A"
          }), ", ", _jsx(_components.code, {
            children: "Brand B"
          }), ", ", _jsx(_components.code, {
            children: "UK"
          }), ", ", _jsx(_components.code, {
            children: "US"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Enforce Space-level filters or required subsets (for example, ", _jsx(_components.code, {
            children: "brand_code"
          }), ", ", _jsx(_components.code, {
            children: "region_code"
          }), ") so each team only works with its own data"]
        }), "\n", _jsx(_components.li, {
          children: "Use a Global Space for cross-brand analytics, experimentation, and coordination of global campaigns"
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "multi-team-organization-separate-teams-shared-customers",
        children: "Multi-team organization (separate teams, shared customers)"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Scenario:"
        }), " Multiple teams (for example, Lifecycle, Paid Media, CRM) operate on the same underlying customers and schema, but need to work independently without affecting each other's campaigns."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Pattern:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Create one Space per team or function"
        }), "\n", _jsx(_components.li, {
          children: "Keep schema and models shared at the workspace level"
        }), "\n", _jsx(_components.li, {
          children: "Use Spaces to isolate campaign assets (audiences and syncs)"
        }), "\n", _jsxs(_components.li, {
          children: ["Apply clear naming conventions to reflect both Space and use case — for example, ", _jsx(_components.code, {
            children: "[Lifecycle] Winback – 90d Lapsed"
          }), " or ", _jsx(_components.code, {
            children: "[Paid Media] High LTV Prospecting"
          })]
        }), "\n", _jsx(_components.li, {
          children: "For audiences that multiple teams share, standardize them in a Global Space or coordinate using Warehouse Sync Logs"
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "naming-conventions",
        children: "Naming conventions"
      }), "\n", _jsx(_components.p, {
        children: "As your use of Spaces scales, consistent naming becomes important for auditability and onboarding."
      }), "\n", _jsx(_components.p, {
        children: "Recommended practices:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Include the Space name in audience and sync names"
          }), " — for example, ", _jsx(_components.code, {
            children: "[UK]"
          }), ", ", _jsx(_components.code, {
            children: "[Brand A]"
          }), ", ", _jsx(_components.code, {
            children: "[Lifecycle]"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use consistent prefixes for test or experiment assets"
          }), " — for example, ", _jsx(_components.code, {
            children: "[TEST]"
          }), ", ", _jsx(_components.code, {
            children: "[EXPERIMENT]"
          })]
        }), "\n", _jsx(_components.li, {
          children: "These practices help prevent sprawl, support audit trails, and make it easier to onboard new teams"
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "current-limitations",
        children: "Current limitations"
      }), "\n", _jsx(_components.p, {
        children: "Spaces v1 is focused on a core set of capabilities for design-partner customers. The following are not yet supported:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Per-Space schema isolation (schema and models are shared workspace-wide)"
        }), "\n", _jsx(_components.li, {
          children: "Space-aware Journeys, Dashboards, or Charts"
        }), "\n", _jsx(_components.li, {
          children: "Selectively hiding parts of the schema per Space"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If your requirements fall outside what's described here, share them with your Hightouch team so they can feed them into product planning."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "related-docs",
        children: "Related docs"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/governance",
            children: "Governance best practices"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/customer-studio/subsets",
            children: "Subsets"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: "Destination Rules"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/user-management",
            children: "User groups"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/roles",
            children: "Roles"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/audit-logs",
            children: "Audit logs"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
